import PropTypes from "prop-types"
import React, { useEffect, useState } from "react"

import BasePopover from "src/components/BasePopover"
import BaseSlider from "src/components/BaseSlider"
import Button from "src/components/Button"
import Form from "src/components/Form"
import SearchableSelect from "src/components/SearchableSelect"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "src/components/Select"
import Tooltip from "src/components/Tooltip"

import useDebounce from "src/hooks/use_debounce"
import useWindowSize from "src/hooks/use_window_size"

import { inchesToFeet } from "src/utils/UnitConversion"
import { snakecaseToTitlecase } from "src/utils/string_helpers"

const MIN_LOA = 0

export const TRANSACTION_TYPE_MAPPING = {
  lt_wet_storage: "Long Term Wet Storage",
  dry_storage: "Dry Storage",
  winter_storage: "Winter Storage",
  maintenance_repairs: "Maintenance / Repairs",
  boat_rental: "Boat Rental",
  ship_store: "Ship Store",
  fuel_sales: "Fuel Sales",
  st_wet_storage: "Short Term Wet Storage",
  other: "Other",
}

const Filters = ({
  table,
  inquiryStatuses,
  inquiryTransactionTypes,
  longestLoa,
  contractsEnabled,
  groups,
  archivedContractGroupsPath,
  onOpenSubmissionFormModal,
}) => {
  const { isXLScreen } = useWindowSize()
  const MAX_LOA = longestLoa ? inchesToFeet(longestLoa) : 200

  const [searchInputValue, setSearchInputValue] = useState(
    table.filters.search || ""
  )
  const [loaRange, setLoaRange] = useState([
    table.filters.loaMin ?? MIN_LOA,
    table.filters.loaMax ?? MAX_LOA,
  ])
  const [debouncedSetSearch] = useDebounce((value) => {
    table.setFilter("search", value)
  })

  useEffect(() => {
    if (!table.filters.search) setSearchInputValue("")
  }, [table.filters.search])

  useEffect(() => {
    if (table.filters.loaMin == null && table.filters.loaMax == null) {
      setLoaRange([MIN_LOA, MAX_LOA])
    }
  }, [table.filters.loaMin, table.filters.loaMax, MAX_LOA])

  const handleSearchChange = (event) => {
    const value = event.target.value
    setSearchInputValue(value)
    debouncedSetSearch(value.trim())
  }

  const handleLoaRangeChange = (values) => {
    setLoaRange(values)
    table.setFilters({
      loaMin: values[0] === MIN_LOA ? null : values[0],
      loaMax: values[1] === MAX_LOA ? null : values[1],
    })
  }

  const handleLoaMinInput = (event) => {
    const value = event.target.value ? parseInt(event.target.value) : null
    table.setFilter("loaMin", value)
  }

  const handleLoaMaxInput = (event) => {
    const value = event.target.value ? parseInt(event.target.value) : null
    table.setFilter("loaMax", value)
  }

  const renderSearch = () => (
    <Form.IconTextField
      icon={
        <i className="fa-solid fa-magnifying-glass text-xl text-gray-400" />
      }
      id="inquiry-search"
      isClearable
      onChange={handleSearchChange}
      onClearSelection={() => {
        setSearchInputValue("")
        table.setFilter("search", "")
      }}
      position="left"
      value={searchInputValue}
      placeholder="Search for a lead by captain or boat name"
    />
  )

  const renderStatusFilter = () => (
    <Select
      multiple
      value={table.filters.statuses}
      onValueChange={(values) => table.setFilter("statuses", values)}
    >
      <SelectTrigger variant="button" className="w-full" aria-label="Status">
        Status
      </SelectTrigger>
      <SelectContent className="max-h-none">
        {inquiryStatuses.map((status) => (
          <SelectItem key={status} value={status}>
            {snakecaseToTitlecase(status)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )

  const renderCategoryFilter = () => (
    <Select
      multiple
      value={table.filters.categories}
      onValueChange={(values) => table.setFilter("categories", values)}
    >
      <SelectTrigger variant="button" className="w-full" aria-label="Category">
        Category
      </SelectTrigger>
      <SelectContent className="max-h-none">
        {inquiryTransactionTypes.map((cat) => (
          <SelectItem key={cat} value={cat}>
            {TRANSACTION_TYPE_MAPPING[cat] ?? snakecaseToTitlecase(cat)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )

  const renderLoaFilter = () => (
    <BasePopover label="LOA" width={isXLScreen ? "400px" : "300px"}>
      <div className="grid grid-cols-12 gap-2 space-y-2 px-6 pb-6 pt-8">
        <div className="col-span-12 flex justify-between">
          <span>{MIN_LOA}&apos;</span>
          <span>{MAX_LOA}&apos;</span>
        </div>
        <div className="col-span-12">
          <BaseSlider
            value={loaRange}
            onValueChange={handleLoaRangeChange}
            min={MIN_LOA}
            max={MAX_LOA}
          />
        </div>
        <div className="col-span-6 flex flex-col">
          <Form.Label htmlFor="loaMin">Minimum</Form.Label>
          <Form.IconTextField
            onChange={handleLoaMinInput}
            icon="ft"
            position="right"
            id="loaMin"
            value={table.filters.loaMin ?? ""}
            type="number"
          />
        </div>
        <div className="col-span-6 flex flex-col">
          <div className="flex">
            <Form.Label htmlFor="loaMax">Maximum</Form.Label>
            <div className="ml-2">
              <Tooltip
                text="Dynamically changes based on longest boat in your contact book"
                placement="top"
                variant="dark"
              >
                <i className="fa-solid fa-circle-info" />
              </Tooltip>
            </div>
          </div>
          <Form.IconTextField
            onChange={handleLoaMaxInput}
            id="loaMax"
            icon="ft"
            position="right"
            value={table.filters.loaMax ?? ""}
            type="number"
          />
        </div>
      </div>
    </BasePopover>
  )

  const renderContractGroupsFilter = () => (
    <SearchableSelect
      multiple
      value={table.filters.contractGroups}
      onValueChange={(values) => table.setFilter("contractGroups", values)}
    >
      <SearchableSelect.Trigger
        variant="button"
        className="w-full"
        aria-label="Contract Group"
      >
        Contract Group
      </SearchableSelect.Trigger>
      <SearchableSelect.FilteredList
        options={groups.map((g) => ({ id: g.encodedId, name: g.name }))}
        placeholder="Search by contract group"
        footer={
          <div className="border-t px-4 pb-2 pt-2">
            <span className="text-balance text-sm text-gray-600">
              Archived groups aren&apos;t shown here. Access them on the{" "}
              <a className="font-semibold" href={archivedContractGroupsPath}>
                archived groups page
              </a>
            </span>
          </div>
        }
      />
    </SearchableSelect>
  )

  return (
    <div className="mt-2 flex flex-col px-4 pb-6 lg:p-0 xl:flex-row">
      <div className="order-2 flex w-full flex-col xl:order-1 xl:flex-row">
        <div className="w-full pb-2 xl:mr-2 xl:w-[336px] xl:p-0">
          {renderSearch()}
        </div>
        <div className="flex w-full xl:w-min">
          <div className="flex w-full flex-wrap space-y-2 sm:flex-nowrap sm:space-x-2 sm:space-y-0">
            {renderStatusFilter()}
            {renderCategoryFilter()}
            {renderLoaFilter()}
            {contractsEnabled &&
              groups.length > 0 &&
              renderContractGroupsFilter()}
          </div>
        </div>
      </div>
      <div className="order-1 mb-2 flex w-full xl:order-2 xl:m-0 xl:w-min">
        <Button
          variant="secondary"
          onClick={onOpenSubmissionFormModal}
          className="w-full"
        >
          Customer Submission Forms
        </Button>
      </div>
    </div>
  )
}

Filters.propTypes = {
  table: PropTypes.object.isRequired,
  inquiryStatuses: PropTypes.array.isRequired,
  inquiryTransactionTypes: PropTypes.array.isRequired,
  longestLoa: PropTypes.number,
  contractsEnabled: PropTypes.bool.isRequired,
  groups: PropTypes.array.isRequired,
  archivedContractGroupsPath: PropTypes.string,
  onOpenSubmissionFormModal: PropTypes.func.isRequired,
}

export default Filters
